import { normalizeTeamKitType, DEFAULT_TEAM_KIT_CONFIG } from '../../../constants/teamKitColors';
import { resolveLeagueCountryCodeForFlag } from '../../shared/helpers/leagueCountryToFlagCode.helper';
import type {
  MatchHubMockData,
  MatchHubPhase,
} from '../types/matchHub.types';

export type { RawApiEvent } from './matchHubEvents.helper';
export { mapEventsToHub } from './matchHubEvents.helper';

export type { RawApiLineup } from './matchHubLineups.helper';
export { mapLineupsToHub } from './matchHubLineups.helper';

export interface MatchHubApiTeam {
  id: string;
  externalId: number | null;
  name: string;
  shortName: string | null;
  kitPrimary: string | null;
  kitSecondary: string | null;
  kitType: string | null;
}

export interface MatchHubApiLeague {
  id: string;
  name: string;
  country: string | null;
  season: string | null;
}

export interface MatchHubApiOdds {
  home: number;
  draw: number;
  away: number;
}

export interface MatchHubApiResponse {
  id: string;
  date: string;
  status: string;
  homeScore: number | null;
  awayScore: number | null;
  minute: number | null;
  venue: string | null;
  referee?: string | null;
  venueCapacity?: number | null;
  round?: string | null;
  homeTeam: MatchHubApiTeam;
  awayTeam: MatchHubApiTeam;
  league: MatchHubApiLeague;
  odds: MatchHubApiOdds | null;
}

/** Raw API-Football fixture shape (from h2h or events endpoints). */
export interface RawApiFixture {
  fixture?: { id?: number; date?: string; referee?: string | null };
  league?: { name?: string; country?: string };
  teams?: {
    home?: { id?: number; name?: string };
    away?: { id?: number; name?: string };
  };
  goals?: { home?: number | null; away?: number | null };
}

function parseRoundLabel(round?: string | null): string {
  if (!round) return '';
  const regular = round.match(/^Regular Season\s*-\s*(\d+)$/i);
  if (regular) return `Round ${regular[1]}`;
  const group = round.match(/^Group Stage\s*-\s*(\d+)$/i);
  if (group) return `Matchday ${group[1]}`;
  return round;
}

const EMPTY_POSITION: MatchHubMockData['tablePositions']['home'] = {
  rank: 0,
  played: 0,
  goalsFor: 0,
  goalsAgainst: 0,
  goalDifference: 0,
  points: 0,
};

function mapPhase(status: string): MatchHubPhase {
  if (['FINISHED', 'POSTPONED', 'CANCELLED', 'ABANDONED', 'SUSPENDED'].includes(status)) return 'finished';
  if (['LIVE', 'IN_PLAY', 'PAUSED'].includes(status)) return 'live';
  return 'pre';
}

function kitFromApiTeam(team: MatchHubApiTeam): MatchHubMockData['home']['kit'] {
  if (!team.kitPrimary || !team.kitSecondary) return DEFAULT_TEAM_KIT_CONFIG;
  return {
    primary: team.kitPrimary,
    secondary: team.kitSecondary,
    type: normalizeTeamKitType(team.kitType),
  };
}

export function mapMatchApiToHubData(match: MatchHubApiResponse): MatchHubMockData {
  const phase = mapPhase(match.status);
  const homeScore = match.homeScore ?? 0;
  const awayScore = match.awayScore ?? 0;
  const minute = match.minute ?? 0;

  return {
    id: match.id,
    phase,
    kickoffIso: match.date,
    countdownDisplay: '',
    league: {
      name: match.league?.name ?? '',
      seasonLabel: match.league?.season ?? '',
      roundLabel: parseRoundLabel(match.round),
      matchday: 0,
    },
    home: {
      id: match.homeTeam.id,
      name: match.homeTeam.name,
      shortName: match.homeTeam.shortName ?? match.homeTeam.name,
      kit: kitFromApiTeam(match.homeTeam),
    },
    away: {
      id: match.awayTeam.id,
      name: match.awayTeam.name,
      shortName: match.awayTeam.shortName ?? match.awayTeam.name,
      kit: kitFromApiTeam(match.awayTeam),
    },
    meta: {
      venue: match.venue ?? '',
      referee: match.referee ?? '',
    },
    matchInfo: {
      countryCode: resolveLeagueCountryCodeForFlag(match.league?.country, match.league?.name) ?? '',
      leagueWithCountry: match.league?.country
        ? `${match.league.name}, ${match.league.country}`
        : (match.league?.name ?? ''),
      roundDisplay: '',
      ...(typeof match.venueCapacity === 'number' && match.venueCapacity > 0
        ? { venueCapacity: match.venueCapacity }
        : {}),
    },
    tablePositions: {
      home: { ...EMPTY_POSITION },
      away: { ...EMPTY_POSITION },
    },
    odds1x2: {
      home: match.odds?.home ?? 1,
      draw: match.odds?.draw ?? 1,
      away: match.odds?.away ?? 1,
    },
    form: { home: [], away: [] },
    lineups: {
      isExpected: true,
      home: { formation: '', starting: [], bench: [] },
      away: { formation: '', starting: [], bench: [] },
    },
    coaches: { home: { name: '' }, away: { name: '' } },
    standings: [],
    h2h: { homeWins: 0, draws: 0, awayWins: 0, goalsHome: 0, goalsAway: 0, meetings: [] },
    events: [],
    ...(phase === 'live'
      ? { live: { minute, homeScore, awayScore } }
      : {}),
    ...(phase === 'finished'
      ? { resultLabel: 'FT' as const, live: { minute: 90, homeScore, awayScore } }
      : {}),
  };
}

export function mapH2HToHub(
  payload: { fixtures: RawApiFixture[] } | null,
  homeExtId: number | null,
): MatchHubMockData['h2h'] {
  const empty: MatchHubMockData['h2h'] = {
    homeWins: 0, draws: 0, awayWins: 0, goalsHome: 0, goalsAway: 0, meetings: [],
  };
  if (!payload?.fixtures?.length) return empty;

  let homeWins = 0, draws = 0, awayWins = 0;
  let goalsHome = 0, goalsAway = 0;

  const meetings: MatchHubMockData['h2h']['meetings'] = payload.fixtures.map((f) => {
    const fHomeId = f.teams?.home?.id;
    const homeGoals = f.goals?.home ?? 0;
    const awayGoals = f.goals?.away ?? 0;

    const fixtureHomeIsOurHome = homeExtId != null && fHomeId === homeExtId;
    const ourGoals = fixtureHomeIsOurHome ? homeGoals : awayGoals;
    const theirGoals = fixtureHomeIsOurHome ? awayGoals : homeGoals;

    goalsHome += ourGoals;
    goalsAway += theirGoals;
    if (ourGoals > theirGoals) homeWins++;
    else if (ourGoals < theirGoals) awayWins++;
    else draws++;

    const countryCode =
      resolveLeagueCountryCodeForFlag(f.league?.country, f.league?.name) ?? undefined;

    return {
      date: (f.fixture?.date ?? '').split('T')[0],
      competition: f.league?.name ?? '',
      competitionCountryCode: countryCode,
      homeTeam: f.teams?.home?.name ?? '',
      awayTeam: f.teams?.away?.name ?? '',
      homeScore: homeGoals,
      awayScore: awayGoals,
    };
  });

  return { homeWins, draws, awayWins, goalsHome, goalsAway, meetings };
}
